/**
 * Admin dashboard — lives inside the game client
 * and is styled like every other tab: wooden panels, pixel font, gold accents.
 *
 * This module owns the sub-nav and mounting/tearing down one section at a time;
 * each section lives in its own file. The Admin tab is only rendered for
 * `role === 'ADMIN'` (see bootstrap.ts) — the real guard is server-side.
 */
import { el, clear } from '../../dom';
import { closeAllModals } from './ui';
import type { GameCtx, ViewTeardown } from '../../types';
import { renderOverview } from './overview';
import { renderPlayers } from './players';
import { renderPurchases } from './purchases';
import { renderTransactions } from './transactions';
import { renderActivity } from './activity';
import { renderEconomy } from './economy';
import { renderCms } from './cms';
import { renderPayouts } from './payouts';
import { renderInvoices } from './invoices';
import { renderSeo } from './seo';
import { renderSeasons } from './seasons';
import { renderApiKeys } from './api-keys';
import { renderSettings } from './settings';
import { renderPaymentsConfig } from './payments-config';
import { mountCompanyFooter } from '../../ui/company-footer';
import { ADMIN_SECTION_EVENT } from './section-event';

type SectionRenderer = (host: HTMLElement) => () => void;

const SECTIONS: { id: string; label: string; render: SectionRenderer }[] = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'players', label: 'Players', render: renderPlayers },
  { id: 'purchases', label: 'Purchases', render: renderPurchases },
  { id: 'transactions', label: 'Transactions', render: renderTransactions },
  { id: 'activity', label: 'Activity log', render: renderActivity },
  { id: 'economy', label: 'Economy & server', render: renderEconomy },
  { id: 'payments', label: 'Payments', render: renderPaymentsConfig },
  { id: 'payouts', label: 'Payouts & KYC', render: renderPayouts },
  { id: 'invoices', label: 'Invoices', render: renderInvoices },
  { id: 'cms', label: 'CMS', render: renderCms },
  { id: 'seasons', label: 'Seasons', render: renderSeasons },
  { id: 'seo', label: 'SEO', render: renderSeo },
  { id: 'keys', label: 'API keys', render: renderApiKeys },
  { id: 'settings', label: 'Settings', render: renderSettings },
];

const STORE_KEY = 'tb.admin.section';

export function renderAdmin(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);

  // Defence in depth: the tab is hidden for non-admins, but a stale route must
  // not paint an admin shell either.
  if (ctx.user?.role !== 'ADMIN') {
    root.appendChild(el('div', 'game-font text-sm text-text-dim py-10 text-center', {},
      'This area is for administrators only.'));
    return () => {};
  }

  const subnav = el('nav', 'game-font flex gap-1 flex-wrap mb-4');
  const body = el('div', 'min-h-[120px]');
  const footer = el('div', 'mt-6');

  const wrap = el('div', 'flex flex-col', {},
    el('h2', 'game-font text-lg text-[#ffd97a] mb-3', {}, 'Admin'),
    subnav, body, footer);
  root.appendChild(wrap);

  const buttons = new Map<string, HTMLButtonElement>();
  let stopSection: (() => void) | null = null;

  function setSection(id: string) {
    // A dialog belongs to the section that opened it. Switching tab with one open
    // used to leave it floating over the next screen.
    closeAllModals();
    stopSection?.();
    stopSection = null;
    clear(body);
    for (const [sid, b] of buttons) b.className = tabClass(sid === id);
    const section = SECTIONS.find((s) => s.id === id) ?? SECTIONS[0];
    try { localStorage.setItem(STORE_KEY, section.id); } catch { /* private mode */ }
    try {
      stopSection = section.render(body);
    } catch {
      clear(body);
      body.appendChild(el('div', 'game-font text-sm text-error py-6 text-center', {},
        'This section failed to open.'));
    }
  }

  for (const s of SECTIONS) {
    const b = el('button', tabClass(false), { type: 'button', onClick: () => setSection(s.id) }, s.label);
    buttons.set(s.id, b);
    subnav.appendChild(b);
  }

  const onSection = (e: Event) => {
    const id = (e as CustomEvent<string>).detail;
    if (SECTIONS.some((s) => s.id === id)) setSection(id);
  };
  document.addEventListener(ADMIN_SECTION_EVENT, onSection);

  const stopFooter = mountCompanyFooter(footer);

  let initial = SECTIONS[0].id;
  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved && SECTIONS.some((s) => s.id === saved)) initial = saved;
  } catch { /* private mode */ }
  setSection(initial);

  return () => {
    // Modals live on document.body, so removing `wrap` does not remove them —
    // see closeAllModals(). Without this, being signed out mid-edit left the
    // editor stranded on the landing page.
    closeAllModals();
    stopSection?.();
    stopFooter();
    document.removeEventListener(ADMIN_SECTION_EVENT, onSection);
    if (root.contains(wrap)) root.removeChild(wrap);
  };
}

function tabClass(active: boolean): string {
  return 'tb-pixel-btn px-3 py-1.5 text-xs' + (active ? ' !text-[#ffd97a] !border-[#e8c15a]' : '');
}
